import { useRef, useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import withStyles from 'beam/dist/styles'
import Typography from 'beam/dist/Typography'
import ChatText from './ChatText'
import { withTranslation } from 'react-i18next'
import { SelectedChatBubbleContext } from '../ChatMapPanel/constants'
import { useLocalizedMoment } from '../../../moment/useLocalizedMoment'
import { CONTACT_TYPE } from '../MediaPlayer/mediaPlayer.constants'
import { formatDuration } from '../../../Common/Utils'

const dataTestIdPrefix = 'ChatBubble-'

const styles = theme => ({
  bubble: {
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    maxWidth: `calc(100% - ${theme.spacing(12)}px)`,
    color: theme.palette.text.primary,
    transform: 'scale(0.975)',
    cursor: 'pointer',
  },
  bubbleAgent: {
    borderRadius: `${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px 0 ${theme.shape.borderRadius *
      4}px`,
    background: theme.palette.color.lightBlue[800],
    alignSelf: 'flex-end',
  },
  bubbleCustomer: {
    borderRadius: `${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius *
      4}px 0`,
    background: theme.palette.grey['300'],
    alignSelf: 'flex-start',
  },
  timestamp: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
  },
  participant: {
    marginRight: 'auto',
    color: theme.palette.text.secondary,
  },
  lightLabel: {
    color: theme.palette.primary.contrastText,
    opacity: 0.54,
  },
  footer: {
    display: 'flex',
    paddingTop: theme.spacing(0.5),
  },
  headers: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  isActive: {
    boxShadow: `0 0 0 3px ${theme.palette.color.red[700]}`,
    transform: 'scale(1)',
  },
})

/**
 * Single message divided into clumps with participant and timestamp
 */

const ChatBubble = ({ index, id, start, clumps, classes, authorLabel, isSelectedAgent, callStartTimeMs, queryCurrentIndex, color, contactTz, contactType }) => {

  const variant = isSelectedAgent ? 'light' : 'dark'

  const bubbleRef = useRef({})
  const [isCurrentBubble, setIsCurrentBubble] = useState(false)
  const { selectedBubbleId, setSelectedBubbleId } = useContext(SelectedChatBubbleContext)
  const { formatDateTime } = useLocalizedMoment()

  useEffect(() => {
    // Scroll to the bubble if it's text is the current search result selection
    for (const clump of clumps) {
      if (clump.qIndex === queryCurrentIndex) {
        bubbleRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
        break
      }
    }

    if (index === selectedBubbleId) {
      // Scroll to selected bubble, but only if we're not performing a search
      if (queryCurrentIndex <= 0) {
        bubbleRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
      setIsCurrentBubble(true)
    } else {
      setIsCurrentBubble(false)
    }
  }, [index, selectedBubbleId, clumps, queryCurrentIndex])

  function onClick() {
    setSelectedBubbleId(index)
  }

  return (
    <div
      className={classNames(classes.bubble, isSelectedAgent ? classes.bubbleAgent : classes.bubbleCustomer, {
        [classes.isActive]: isCurrentBubble,
      })}
      ref={bubbleRef}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onClick}
      style={{ background: color }}
    >
      <div className={classes.headers}>
        <Typography
          className={classNames([classes.participant, isSelectedAgent && classes.lightLabel])}
          align="right"
          variant="caption"
          data-testid={`${dataTestIdPrefix}Header-${id}`}
        >
          {authorLabel}
        </Typography>
        {contactType === CONTACT_TYPE.EMAIL && <div className={classes.headerAndFooter}>
          <Typography
            className={classNames([classes.participant, isSelectedAgent && classes.lightLabel])}
            align="right"
            variant="caption"
          >
            {formatDateTime(start, 'L LT', contactTz)}
          </Typography>
        </div>
        }
      </div>
      <ChatText
        clumps={clumps}
        queryCurrentIndex={queryCurrentIndex}
        variant={variant}
      />
      <div className={classes.footer}>
        <Typography
          className={classNames([classes.timestamp, isSelectedAgent && classes.lightLabel])}
          align="right"
          variant="caption"
          data-testid={`${dataTestIdPrefix}Footer-${id}`}
        >
          {formatDuration(start - callStartTimeMs)}
        </Typography>
      </div>
    </div>
  )
}

ChatBubble.propTypes = {
  index: PropTypes.number.isRequired,
  start: PropTypes.number.isRequired,
  clumps: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  authorLabel: PropTypes.string.isRequired,
  callStartTimeMs: PropTypes.number.isRequired,
  isSelectedAgent: PropTypes.bool.isRequired,
  queryCurrentIndex: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  color: PropTypes.string,
  contactTz: PropTypes.string.isRequired,
  contactType: PropTypes.string.isRequired,
}

export { ChatBubble as UnstyledChatBubble }
export default withTranslation()(withStyles(styles)(ChatBubble))
